// src/hooks/useSEO.js
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const useSEO = () => {
  const location = useLocation();
  
  useEffect(() => {
    const seoData = {
      '/': {
        title: 'NEVXOAULIO VENTURES - Technology Solutions Provider | India',
        description: 'Leading technology solutions provider specializing in Software Development, Cloud Computing, Cyber Security, VLSI Design, AI/ML Solutions, and Data Analytics. 50+ projects delivered, 15+ experts.',
        keywords: 'technology solutions, software development, AI ML, cloud computing, cybersecurity, VLSI design, data analytics, IT services India, technology company Tumkur',
        ogImage: 'https://www.nevxoaulio.com/logo/og-image.png',
        twitterCard: 'summary_large_image'
      },
      '/about': {
        title: 'About Us - NEVXOAULIO VENTURES | Technology Company',
        description: 'Learn about Nevxoaulio Ventures, our mission, team, and expertise in delivering cutting-edge technology solutions in India.',
        keywords: 'about nevxoaulio, company history, our team, technology experts, India tech company',
        ogImage: 'https://www.nevxoaulio.com/logo/og-image.png'
      },
      '/contact': {
        title: 'Contact Us - NEVXOAULIO VENTURES | Get in Touch',
        description: 'Contact Nevxoaulio Ventures for technology solutions, software development, cloud services, and AI/ML projects.',
        keywords: 'contact nevxoaulio, get quote, technology consultation, project inquiry'
      },
      '/portfolio': {
        title: 'Our Portfolio - NEVXOAULIO VENTURES | Project Showcase',
        description: 'View our portfolio of successful technology projects including software development, cloud migration, and AI solutions.',
        keywords: 'portfolio, case studies, projects, success stories, technology projects'
      },
      '/faq': {
        title: 'FAQ - NEVXOAULIO VENTURES | Frequently Asked Questions',
        description: 'Find answers to frequently asked questions about our technology services, pricing, and project process.',
        keywords: 'FAQ, questions, technology services FAQ, common questions'
      },
      '/sitemap': {
        title: 'Sitemap - NEVXOAULIO VENTURES | Complete Site Structure',
        description: 'Complete sitemap of Nevxoaulio Ventures website with all pages and services.',
        keywords: 'sitemap, website structure, navigation map'
      },
      '/services': {
        title: 'Our Services - NEVXOAULIO VENTURES | IT Solutions',
        description: 'Comprehensive technology services including software development, cloud solutions, cybersecurity, VLSI design, AI/ML, and data analytics.',
        keywords: 'IT services, software development, cloud services, cybersecurity solutions, AI ML services'
      },
      '/services/software-development': {
        title: 'Software Development Services | Custom Solutions | NEVXOAULIO',
        description: 'Custom software development services for web, mobile, desktop applications with modern technologies.',
        keywords: 'software development, custom software, web development, mobile apps, enterprise software'
      },
      '/services/cloud-computing': {
        title: 'Cloud Computing Solutions | AWS, Azure, Google Cloud',
        description: 'Cloud migration, deployment, and management services for AWS, Azure, and Google Cloud Platform.',
        keywords: 'cloud computing, AWS, Azure, Google Cloud, cloud migration, cloud services'
      },
      '/services/cyber-security': {
        title: 'Cyber Security Services | Protection & Compliance',
        description: 'Complete cybersecurity solutions including penetration testing, compliance, and threat protection.',
        keywords: 'cybersecurity, security services, penetration testing, compliance, data protection'
      },
      '/services/vlsi-design': {
        title: 'VLSI Design Services | Semiconductor & Chip Design',
        description: 'VLSI design and verification services for semiconductor companies and hardware startups.',
        keywords: 'VLSI design, semiconductor, chip design, hardware design, ASIC, FPGA'
      },
      '/services/ai-ml-solutions': {
        title: 'AI & ML Solutions | Artificial Intelligence Services',
        description: 'AI and Machine Learning solutions including predictive analytics, computer vision, and NLP.',
        keywords: 'AI solutions, machine learning, artificial intelligence, predictive analytics, computer vision'
      },
      '/services/data-analytics': {
        title: 'Data Analytics Services | Business Intelligence',
        description: 'Data analytics and business intelligence solutions for data-driven decision making.',
        keywords: 'data analytics, business intelligence, data visualization, data science'
      },
      '/blog': {
        title: 'Technology Blog - NEVXOAULIO VENTURES | Insights',
        description: 'Latest insights on software development, AI, cloud computing, cybersecurity, and technology trends.',
        keywords: 'technology blog, tech insights, software blog, AI blog, cloud blog'
      },
      '/careers': {
        title: 'Careers - Join NEVXOAULIO VENTURES | Tech Jobs',
        description: 'Career opportunities at Nevxoaulio Ventures. Join our team of technology experts.',
        keywords: 'careers, jobs, technology jobs, software jobs, career opportunities'
      },
      '/forum': {
        title: 'Community Forum - NEVXOAULIO VENTURES | Tech Discussions',
        description: 'Join our technology community forum for discussions, Q&A, and knowledge sharing.',
        keywords: 'forum, community, tech discussions, Q&A, knowledge sharing'
      },
      '/feedback': {
        title: 'Customer Feedback - NEVXOAULIO VENTURES',
        description: 'Share your feedback about our services and help us improve.',
        keywords: 'feedback, customer reviews, testimonials, suggestions'
      },
      '/privacy': {
        title: 'Privacy Policy - NEVXOAULIO VENTURES',
        description: 'Privacy policy outlining how we collect, use, and protect your data.',
        keywords: 'privacy policy, data protection, privacy terms'
      },
      '/terms': {
        title: 'Terms of Service - NEVXOAULIO VENTURES',
        description: 'Terms and conditions for using Nevxoaulio Ventures website and services.',
        keywords: 'terms of service, terms and conditions, legal terms'
      }
      // Dynamic pages like /blog/:id will be handled separately
    };

    const path = location.pathname;
    const data = seoData[path] || seoData['/']; // Fallback to home

    // Update title
    document.title = data.title;

    // Helper function to update/create meta tags
    const updateMetaTag = (name, content, attribute = 'name') => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // Update meta description
    updateMetaTag('description', data.description);
    
    // Update meta keywords
    updateMetaTag('keywords', data.keywords);
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://www.nevxoaulio.com${path}`;

    // Update OG tags
    updateMetaTag('og:title', data.title, 'property');
    updateMetaTag('og:description', data.description, 'property');
    updateMetaTag('og:url', `https://www.nevxoaulio.com${path}`, 'property');
    updateMetaTag('og:type', path === '/' ? 'website' : 'article', 'property');
    updateMetaTag('og:image', data.ogImage || 'https://www.nevxoaulio.com/logo/og-image.png', 'property');
    updateMetaTag('og:site_name', 'NEVXOAULIO VENTURES', 'property');
    updateMetaTag('og:locale', 'en_IN', 'property');

    // Update Twitter tags
    updateMetaTag('twitter:card', data.twitterCard || 'summary_large_image');
    updateMetaTag('twitter:title', data.title);
    updateMetaTag('twitter:description', data.description);
    updateMetaTag('twitter:image', data.ogImage || 'https://www.nevxoaulio.com/logo/og-image.png');
    updateMetaTag('twitter:site', '@NevxoaulioTech');
    updateMetaTag('twitter:creator', '@NevxoaulioTech');

    updateHreflangLinks(path);

    if (path === '/') {
      addStructuredData();
    }

  }, [location.pathname]);
};

const updateHreflangLinks = (path) => {
  const baseUrl = 'https://www.nevxoaulio.com';
  const hreflangs = [
    { lang: 'en', url: `${baseUrl}${path}` },
    { lang: 'en-in', url: `${baseUrl}${path}` },
    { lang: 'x-default', url: `${baseUrl}${path}` }
  ];

  hreflangs.forEach(({ lang, url }) => {
    let link = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
    if (!link) {
      link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      document.head.appendChild(link);
    }
    link.href = url;
  });
};

// Helper function for structured data
const addStructuredData = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "NEVXOAULIO VENTURES",
    "url": "https://www.nevxoaulio.com",
    "logo": "https://www.nevxoaulio.com/logo/company-logo.png",
    "description": "Leading technology solutions provider specializing in Software Development, Cloud Computing, Cyber Security, VLSI Design, AI/ML Solutions, and Data Analytics."
  };
  
  let script = document.querySelector('script[type="application/ld+json"]');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(structuredData);
};

export default useSEO;